// Cookie the marketing site (Framer) sets on .cavela.com on a visitor's
// landing page, while the ad click id, UTM tags and referrer are still known.
const ATTRIBUTION_COOKIE_NAME = 'cv_attr';

const ATTRIBUTION_KEYS = [
  'gclid',
  // Google Ads click ids for iOS traffic, sent instead of gclid
  'gbraid',
  'wbraid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'referrer'
] as const;

// backend/cavela/serializers.py > UserCreationSerializer
export type SignupAttribution = Record<
  (typeof ATTRIBUTION_KEYS)[number],
  string
>;

const readAttributionCookie = (cookie: string): Partial<SignupAttribution> => {
  const entry = cookie
    .split('; ')
    .find((part) => part.startsWith(`${ATTRIBUTION_COOKIE_NAME}=`));

  if (!entry) return {};
  try {
    const parsed: unknown = JSON.parse(
      decodeURIComponent(entry.slice(ATTRIBUTION_COOKIE_NAME.length + 1))
    );

    if (typeof parsed !== 'object' || parsed === null) return {};
    const values = parsed as Record<string, unknown>;

    return Object.fromEntries(
      ATTRIBUTION_KEYS.filter((key) => typeof values[key] === 'string').map(
        (key) => [key, values[key] as string]
      )
    );
  } catch {
    // The cookie is written by another site; a malformed one must not block signup.
    return {};
  }
};

/**
 * How the visitor reached the signup form, sent with the registration so the
 * backend can record it and derive the org's lead source.
 *
 * Starts from the marketing site's cookie, and lets click ids and UTM tags on
 * the register URL itself (links that carry them straight into the app) take
 * precedence. The referrer only comes from the cookie: here it would be the
 * marketing site.
 */
export const getSignupAttribution = (
  searchParams: URLSearchParams,
  cookie: string
): SignupAttribution => {
  const fromCookie = readAttributionCookie(cookie);

  return Object.fromEntries(
    ATTRIBUTION_KEYS.map((key) => [
      key,
      (key !== 'referrer' && searchParams.get(key)) || fromCookie[key] || ''
    ])
  ) as SignupAttribution;
};
